import { Component, ElementRef, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { Inject, PLATFORM_ID } from '@angular/core';
import { AfrobotService } from './afrobot.service';
import { UserService } from '../../../user.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface ChatMessage {
  from: 'user' | 'bot' | 'typing' | 'login-prompt';
  text: string;
  timestamp: Date;
  id?: string;
  edited?: boolean;
  editedAt?: Date;
  feedback?: 'like' | 'dislike' | null;  // Nouveau
}

@Component({
  selector: 'app-afrobot',
  templateUrl: './afrobot.component.html',
  styleUrls: ['./afrobot.component.scss']
})
export class AfrobotComponent implements OnInit, AfterViewInit, OnDestroy {
  isOpen = false;
  userMessage = '';
  welcomeMessage = '';
  messages: ChatMessage[] = [];
  sessionId: string = '';
  isLoading = false;
  hasUnreadMessages = false;

  editingMessageId: string | null = null;
  editingText: string = '';

  maxQuestionsForGuests = 5;
  maxQuestionsLogged = 15;
  userQuestionCount = 0;
  guestUsedCount = 0;
  isLoggedIn = false;

  private destroy$ = new Subject<void>();

  @ViewChild('chatEnd') chatEnd!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;
  @ViewChild('editTextarea') editTextarea!: ElementRef<HTMLTextAreaElement>;

  constructor(
    private router: Router,
    private afrobotService: AfrobotService,
    private userService: UserService,
    private sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.sessionId = this.generateSessionId();
  }

  ngOnInit(): void {
    this.isLoggedIn = this.userService.isLoggedIn();

    if (isPlatformBrowser(this.platformId)) {
      const guestCount = localStorage.getItem('afrobot_guestQuestionCount');
      this.guestUsedCount = guestCount ? parseInt(guestCount, 10) : 0;

      if (this.isLoggedIn) {
        this.loadUserQuestionCount();
      }
    }

    this.userService.isLoggedIn$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(loggedIn => {
      const wasLoggedIn = this.isLoggedIn;
      this.isLoggedIn = loggedIn;
      this.removeLoginPrompt();

      if (!loggedIn) {
        this.isOpen = false;
        if (isPlatformBrowser(this.platformId)) {
          const guestCount = localStorage.getItem('afrobot_guestQuestionCount');
          this.guestUsedCount = guestCount ? parseInt(guestCount, 10) : 0;
        }
      } else if (!wasLoggedIn && loggedIn) {
        this.loadUserQuestionCount();
      }
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      const navigationEvent = event as NavigationEnd;
      this.updateWelcomeMessageForCurrentRoute(navigationEvent.url);
    });

    const currentUrl = this.router.url.toLowerCase();
    this.calculateWelcomeMessage(currentUrl);
  }

  private loadUserQuestionCount(): void {
    const userId = this.userService.getUserId();
    if (userId) {
      this.afrobotService.getUserQuestionCount(userId).subscribe(count => {
        this.userQuestionCount = count;
      });
    }
  }

  // ========== NOUVELLE FONCTIONNALITÉ: Feedback Like/Dislike ==========

  giveFeedback(messageId: string, feedback: 'like' | 'dislike'): void {
    const message = this.messages.find(m => m.id === messageId);
    if (!message || message.from !== 'bot') return;

    // Réinitialiser le timer d'inactivité
    if (this.isLoggedIn) {
      this.userService.startInactivityTimer();
    }

    // Toggle: si déjà le même feedback, on l'enlève
    if (message.feedback === feedback) {
      message.feedback = null;
    } else {
      message.feedback = feedback;
    }

    // Sauvegarder dans localStorage pour persistance
    if (isPlatformBrowser(this.platformId)) {
      this.saveFeedbackToStorage(messageId, message.feedback);
    }

    // Envoyer au backend (optionnel)
    this.sendFeedbackToBackend(messageId, message.feedback);
  }

  private saveFeedbackToStorage(messageId: string, feedback: 'like' | 'dislike' | null): void {
    const feedbackKey = `afrobot_feedback_${messageId}`;
    if (feedback) {
      localStorage.setItem(feedbackKey, feedback);
    } else {
      localStorage.removeItem(feedbackKey);
    }
  }

  private sendFeedbackToBackend(messageId: string, feedback: 'like' | 'dislike' | null): void {
    // TODO: Implémenter l'appel API vers votre backend
    console.log(`Feedback envoyé: Message ${messageId} - ${feedback}`);

    // Exemple d'implémentation:
    // this.afrobotService.sendFeedback(messageId, feedback, this.sessionId).subscribe({
    //   next: () => console.log('Feedback enregistré'),
    //   error: (err) => console.error('Erreur feedback:', err)
    // });
  }

  // ========== Méthodes existantes améliorées ==========

  isLastUserMessage(messageId: string): boolean {
    const userMessages = this.messages.filter(msg => msg.from === 'user');
    return userMessages.length > 0 && userMessages[userMessages.length - 1].id === messageId;
  }

  startEditMessage(messageId: string): void {
    if (this.isLoading || this.editingMessageId) return;

    const message = this.messages.find(m => m.id === messageId);
    if (!message) return;

    // Réinitialiser le timer d'inactivité
    if (this.isLoggedIn) {
      this.userService.startInactivityTimer();
    }

    this.editingMessageId = messageId;
    this.editingText = message.text;

    setTimeout(() => {
      if (this.editTextarea) {
        const textarea = this.editTextarea.nativeElement;
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        this.autoResizeTextarea(textarea);
      }
    }, 100);
  }

  private updateWelcomeMessageForCurrentRoute(url: string): void {
    this.calculateWelcomeMessage(url.toLowerCase());

    if (this.isOpen) {
      this.updateWelcomeMessageInChat();
    }
  }

  saveEdit(): void {
    if (!this.editingMessageId || !this.editingText.trim()) return;

    const message = this.messages.find(m => m.id === this.editingMessageId);
    if (!message) return;

    const oldText = message.text;
    message.text = this.editingText.trim();
    message.edited = true;
    message.editedAt = new Date();

    this.removeSubsequentBotMessages(this.editingMessageId);

    this.editingMessageId = null;
    this.editingText = '';

    if (oldText !== message.text && this.canAskQuestion()) {
      this.sendModifiedMessage(message.text);
    }
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editingText = '';
  }

  retryMessage(messageId: string): void {
    if (!this.canAskQuestion() || this.isLoading) return;

    const message = this.messages.find(m => m.id === messageId);
    if (!message) return;

    this.removeSubsequentBotMessages(messageId);
    this.sendModifiedMessage(message.text);
  }

  private sendModifiedMessage(text: string): void {
    this.isLoading = true;
    this.showTypingIndicator();

    this.afrobotService.askAfrobotN8n(text, this.sessionId).subscribe({
      next: (response) => {
        this.hideTypingIndicator();

        if (!response || !response.reply || response.reply.trim() === '') {
          throw new Error('Réponse vide du serveur');
        }

        this.messages.push({
          from: 'bot',
          text: response.reply,
          timestamp: new Date(),
          id: this.generateMessageId(),
          feedback: null
        });

        if (!this.canAskQuestion()) {
          setTimeout(() => this.showLoginPromptMessage(), 500);
        }

        if (response.sessionId) this.sessionId = response.sessionId;
        if (!this.isOpen) this.hasUnreadMessages = true;
        this.scrollToBottom();
      },
      error: (error) => {
        this.hideTypingIndicator();
        console.error('Erreur chat n8n lors de la modification:', error);

        const errorMessage = error?.message ||
          'Désolé, je rencontre des difficultés techniques. Veuillez réessayer dans quelques instants.';

        this.messages.push({
          from: 'bot',
          text: errorMessage,
          timestamp: new Date(),
          id: this.generateMessageId(),
          feedback: null
        });

        this.scrollToBottom();
      }
    });
  }

  onEditKeyDown(event: KeyboardEvent): void {
    const textarea = event.target as HTMLTextAreaElement;

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    } else {
      setTimeout(() => this.autoResizeTextarea(textarea), 0);
    }
  }

  autoResizeTextarea(textarea: HTMLTextAreaElement | EventTarget | null): void {
    if (!textarea) return;

    const textareaElement = textarea as HTMLTextAreaElement;
    textareaElement.style.height = 'auto';
    textareaElement.style.height = Math.min(textareaElement.scrollHeight, 120) + 'px';
  }

  private generateMessageId(): string {
    return 'msg-' + Date.now() + '-' + Math.random().toString(36).substr(2, 5);
  }

  private removeSubsequentBotMessages(userMessageId: string): void {
    const userMessageIndex = this.messages.findIndex(m => m.id === userMessageId);
    if (userMessageIndex === -1) return;

    let indexToRemove = userMessageIndex + 1;
    while (indexToRemove < this.messages.length &&
      this.messages[indexToRemove].from !== 'user') {
      this.messages.splice(indexToRemove, 1);
    }
  }

  private updateWelcomeMessageInChat(): void {
    this.messages = this.messages.filter(msg =>
      msg.from !== 'bot' && msg.id !== 'welcome-message'
    );

    if ((this.isLoggedIn && this.remainingLogged === 0) ||
      (!this.isLoggedIn && this.remainingGuest === 0)) {
      this.showLoginPromptMessage();
    } else {
      this.messages.push({
        from: 'bot',
        text: this.welcomeMessage,
        timestamp: new Date(),
        id: 'welcome-message',
        feedback: null
      });
    }

    this.scrollToBottom();
  }

  private calculateWelcomeMessage(url: string): void {
    const messages = {
      'solutions': 'Tu veux en savoir plus sur nos solutions digitales ?',
      'connect': 'Bienvenue sur AfroTech Connect ! Pose-moi tes questions.',
      'learn': 'Envie d\'apprendre ? Je suis là pour t\'aider à comprendre la tech africaine !',
      'contact': 'Tu peux me poser ta question ici, ou utiliser le formulaire à gauche',
      'news': 'Que veux-tu savoir sur l\'actualité Tech africaine ?'
    };

    const foundKey = Object.keys(messages).find(key => url.includes(key));
    this.welcomeMessage = foundKey
      ? messages[foundKey as keyof typeof messages]
      : 'Salut ! Pose-moi ta question sur la tech africaine';
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;

    // Réinitialiser le timer d'inactivité
    if (this.isOpen && this.isLoggedIn) {
      this.userService.startInactivityTimer();
    }

    if (this.isOpen) {
      this.hasUnreadMessages = false;

      this.calculateWelcomeMessage(this.router.url.toLowerCase());

      this.messages = this.messages.filter(msg =>
        msg.from !== 'bot' && msg.id !== 'welcome-message'
      );

      if ((this.isLoggedIn && this.remainingLogged === 0) ||
        (!this.isLoggedIn && this.remainingGuest === 0)) {
        this.showLoginPromptMessage();
      } else {
        this.messages.push({
          from: 'bot',
          text: this.welcomeMessage,
          timestamp: new Date(),
          id: 'welcome-message',
          feedback: null
        });
      }

      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          this.scrollToBottom();
          if (this.messageInput) {
            this.messageInput.nativeElement.focus();
          }
        }, 100);
      }
    }
  }

  clearChat(): void {
    // ✅ Vider complètement les messages au lieu de filtrer
    this.messages = [];

    // Générer une nouvelle session
    this.sessionId = this.generateSessionId();

    // Recalculer le message de bienvenue
    this.calculateWelcomeMessage(this.router.url.toLowerCase());

    // Afficher soit le prompt de connexion, soit le message de bienvenue
    if ((this.isLoggedIn && this.remainingLogged === 0) ||
      (!this.isLoggedIn && this.remainingGuest === 0)) {
      this.showLoginPromptMessage();
    } else {
      this.messages.push({
        from: 'bot',
        text: this.welcomeMessage,
        timestamp: new Date(),
        id: 'welcome-message',
        feedback: null
      });
    }

    this.scrollToBottom();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.scrollToBottom();
    }
  }

  navigateToContact(): void {
    this.isOpen = false;
    this.router.navigate(['/contact']);
  }

  private generateSessionId(): string {
    return 'afrotech-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  get remainingGuest(): number {
    return Math.max(0, this.maxQuestionsForGuests - this.guestUsedCount);
  }

  get remainingLogged(): number {
    return Math.max(0, this.maxQuestionsLogged - this.userQuestionCount);
  }

  canAskQuestion(): boolean {
    return this.isLoggedIn ? this.remainingLogged > 0 : this.remainingGuest > 0;
  }

  get isInputDisabled(): boolean {
    return this.isLoading || !this.canAskQuestion();
  }

  get inputPlaceholder(): string {
    if (!this.canAskQuestion()) {
      if (this.isLoggedIn) {
        return 'Quota de questions atteint - Contactez-nous pour continuer';
      } else {
        return 'Connectez-vous pour continuer...';
      }
    }
    return 'Posez votre question...';
  }

  sendMessageN8n(): void {
    if (!this.userMessage.trim() || this.isLoading) return;

    if (!this.canAskQuestion()) {
      this.showLoginPromptMessage();
      return;
    }

    const question = this.userMessage.trim();

    // Réinitialiser le timer d'inactivité
    if (this.isLoggedIn) {
      this.userService.startInactivityTimer();
    }

    this.messages.push({
      from: 'user',
      text: question,
      timestamp: new Date(),
      id: this.generateMessageId()
    });

    this.userMessage = '';
    this.isLoading = true;
    this.showTypingIndicator();

    this.afrobotService.askAfrobotN8n(question, this.sessionId).subscribe({
      next: (response) => {
        this.hideTypingIndicator();

        if (!response || !response.reply || response.reply.trim() === '') {
          throw new Error('Réponse vide du serveur');
        }

        // Dans sendMessageN8n(), section success
        if (this.isLoggedIn) {
          const userId = this.userService.getUserId();

          this.afrobotService.incrementUserQuestionCount(userId).subscribe(newCount => {
            this.userQuestionCount = newCount;
          });
        } else {
          this.guestUsedCount++;
          localStorage.setItem('afrobot_guestQuestionCount', this.guestUsedCount.toString());
        }

        this.messages.push({
          from: 'bot',
          text: response.reply,
          timestamp: new Date(),
          id: this.generateMessageId(),
          feedback: null
        });

        if (!this.canAskQuestion()) {
          setTimeout(() => this.showLoginPromptMessage(), 500);
        }

        if (response.sessionId) this.sessionId = response.sessionId;
        if (!this.isOpen) this.hasUnreadMessages = true;
        this.scrollToBottom();
      },
      error: (error) => {
        this.hideTypingIndicator();

        console.error('Erreur chat n8n:', error);

        const errorMessage = error?.message ||
          'Désolé, je rencontre des difficultés techniques. Veuillez réessayer dans quelques instants.';

        this.messages.push({
          from: 'bot',
          text: errorMessage,
          timestamp: new Date(),
          id: this.generateMessageId(),
          feedback: null
        });

        this.scrollToBottom();
      }
    });
  }

  private showLoginPromptMessage(): void {
    this.removeLoginPrompt();
    this.messages.push({
      from: 'login-prompt',
      text: '',
      timestamp: new Date(),
      id: 'login-prompt'
    });
    this.userMessage = '';
    this.scrollToBottom();
  }

  private removeLoginPrompt(): void {
    this.messages = this.messages.filter(msg => msg.id !== 'login-prompt');
  }

  navigateToLogin(): void {
    this.isOpen = false;
    this.router.navigate(['/sign-in']);
  }

  navigateToSignup(): void {
    this.isOpen = false;
    this.router.navigate(['/sign-up']);
  }

  private showTypingIndicator(): void {
    this.messages.push({
      from: 'typing',
      text: '',
      timestamp: new Date(),
      id: 'typing-indicator'
    });
    this.scrollToBottom();
  }

  private hideTypingIndicator(): void {
    this.isLoading = false;
    this.messages = this.messages.filter(msg => msg.id !== 'typing-indicator');
  }

  formatMessage(text: string): SafeHtml {
    let processedText = text;

    const markdownLinkRegex = /\[([^\]]*)\]\(([^)]+)\)/g;
    processedText = processedText.replace(markdownLinkRegex, (match, linkText, url) => {
      const cleanUrl = url.trim();
      const displayText = linkText || cleanUrl;

      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" ` +
        `style="color: #f37529; text-decoration: underline; font-weight: 600;" ` +
        `onclick="window.open('${cleanUrl}', '_blank', 'noopener,noreferrer'); return false;">${displayText}</a>`;
    });

    const moreInfoRegex = /(Pour en savoir plus\s*:\s*)\[?[^\]]*\]?\(?([^)\s]+)\)?/gi;
    processedText = processedText.replace(moreInfoRegex, (match, prefix, url) => {
      const cleanUrl = url.trim();

      return `${prefix}<br><a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" ` +
        `style="color: #f37529; text-decoration: underline; font-weight: 600; display: inline-block; margin-top: 5px;" ` +
        `onclick="window.open('${cleanUrl}', '_blank', 'noopener,noreferrer'); return false;">Voir la vidéo</a>`;
    });

    const urlRegex = /(?<!href=")(?<!src=")(https?:\/\/[^\s<>"{}|\\^`\[\]()]+)/g;
    processedText = processedText.replace(urlRegex, (url) => {
      const cleanUrl = url.replace(/[.,;:!?]+$/, '');

      return `<br><a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" ` +
        `style="color: #f37529; text-decoration: underline; font-weight: 600; display: inline-block; margin-top: 5px;" ` +
        `onclick="window.open('${cleanUrl}', '_blank', 'noopener,noreferrer'); return false;">${cleanUrl}</a>`;
    });

    processedText = processedText.replace(/\n/g, '<br>');

    return this.sanitizer.bypassSecurityTrustHtml(processedText);
  }

  setWelcomeMessage(url: string): void {
    const messages = {
      'solutions': 'Tu veux en savoir plus sur nos solutions digitales ?',
      'connect': 'Bienvenue sur AfroTech Connect ! Pose-moi tes questions.',
      'learn': 'Envie d\'apprendre ? Je suis là pour t\'aider à comprendre la tech africaine !',
      'contact': 'Tu peux me poser ta question ici, ou utiliser le formulaire à gauche',
      'news': 'Que veux-tu savoir sur l\'actualité Tech africaine ?'
    };

    const foundKey = Object.keys(messages).find(key => url.includes(key));
    const baseMessage = foundKey
      ? messages[foundKey as keyof typeof messages]
      : 'Salut ! Pose-moi ta question sur la tech africaine';

    if ((this.isLoggedIn && this.remainingLogged === 0) ||
      (!this.isLoggedIn && this.remainingGuest === 0)) {
      this.showLoginPromptMessage();
      this.welcomeMessage = '';
      return;
    }

    this.messages = this.messages.filter(msg => msg.id !== 'welcome-message');

    this.welcomeMessage = baseMessage;

    if (this.isOpen) {
      this.addWelcomeMessageOnce();
    }
  }

  addWelcomeMessageOnce(): void {
    if (this.welcomeMessage) {
      this.messages.push({
        from: 'bot',
        text: this.welcomeMessage,
        timestamp: new Date(),
        id: 'welcome-message',
        feedback: null
      });

      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.scrollToBottom(), 100);
      }
    }
  }

  scrollToBottom(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        if (this.chatEnd) {
          this.chatEnd.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessageN8n();
    }
  }

  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id || index.toString();
  }
}